import VisuallyHidden from "@galaxy-io/dls/a11y/VisuallyHidden";
import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Divider from "@galaxy-io/dls/layout/Divider";
import DlsLink from "@galaxy-io/dls/links/Link";
import Kbd, { KbdSize, KbdVariant } from "@galaxy-io/dls/text/Kbd";
import Text, { TextSize, TextVariant } from "@galaxy-io/dls/text/Text";
import { t } from "@galaxy-io/dls/theme/tokens/t";
import Widget, { WidgetVariant } from "@galaxy-io/dls/widget/Widget";
import type { ActivityItem } from "@gtm/contracts";
import { activityVerb, sourceName } from "@gtm/contracts";
import { type ReviewState, reviewActions, reviewLabels } from "@gtm/contracts/core/review";
import { styled } from "@linaria/react";
import {
  ArrowCounterClockwiseIcon,
  CheckCircleIcon,
  LinkIcon,
  MagnifyingGlassIcon,
} from "@phosphor-icons/react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { DrawerRow, DrawerSection } from "@/components/workspace/drawer-content";
import { Inline } from "@/components/workspace/people-styles";
import { RelativeTime } from "@/components/workspace/relative-time";
import { reviewIcons } from "@/components/workspace/review-status";
import { SourceLabel } from "@/components/workspace/source-icon";
import { SourceMarkdown } from "@/components/workspace/source-markdown";
import { apiClient, unwrap } from "@/lib/api-client";
import { sourceLabel } from "@/lib/source-label";
import { safeUrl } from "@/lib/utils";
import { ActivitySignalChip } from "./activity-signal-chip";
import { CommentContext } from "./comment-context";
import { useReviews } from "./review-actions";
import { SendMenu } from "./send-menu";
import { SignalLoading, SignalPlaceholder } from "./signal-placeholder";
import { SwipePerson } from "./swipe-person";

const Viewport = styled.section`
  flex: 1;
  min-height: 0;
  display: flex;
  overflow: hidden;
  padding: ${t.space[16]} var(--gtm-page-inset, ${t.space[24]}) ${t.space[24]};
`;
const Deck = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  min-width: 0;
  gap: ${t.space[12]};
`;
const Card = styled.article`
  transform: var(--gx-swipe-transform);
  opacity: var(--gx-swipe-opacity);
  display: block;
  overflow: auto;
  flex: 1;
  min-height: 0;
  min-width: 0;
  width: 100%;
  touch-action: pan-y;
  user-select: none;
  cursor: grab;
  overflow-wrap: anywhere;
  transition:
    transform ${t.duration.base} ease,
    opacity ${t.duration.base} ease;
  &[data-dragging="true"] {
    cursor: grabbing;
    transition: none;
  }
  &:focus-visible {
    outline: 2px solid ${t.color.border.focused};
    outline-offset: 4px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${t.space[12]};
  flex-wrap: wrap;
`;
const Content = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(280px, 1fr);
  grid-template-rows: minmax(0, 1fr);
  max-height: 65dvh;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto auto;
    overflow: auto;
  }
`;
const Signal = styled.section`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${t.space[16]};
  min-width: 0;
  min-height: 0;
  padding: ${t.space[16]};
`;
const Description = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  @media (max-width: 900px) {
    flex: none;
    max-height: 320px;
  }
`;
const Person = styled.aside`
  min-width: 0;
  min-height: 0;
  overflow: auto;
  padding: ${t.space[16]};
  border-left: 0.5px solid ${t.color.border.primary};
  background: ${t.color.background.secondary};
  @media (max-width: 900px) {
    border-left: 0;
    border-top: 0.5px solid ${t.color.border.primary};
  }
`;
const Links = styled.div`
  flex-shrink: 0;
`;
const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${t.space[12]};
  padding: ${t.space[16]} ${t.space[16]};
  flex-shrink: 0;
`;
const Help = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${t.space[12]};
  padding: ${t.space[8]} ${t.space[16]};
  flex-shrink: 0;
  flex-wrap: wrap;
`;

export function SignalSwipe({ query }: { query: Record<string, string | undefined> }) {
  const filters = new URLSearchParams();
  for (const key of [
    "q",
    "type",
    "repository",
    "days",
    "time",
    "filter",
    "topic",
    "review",
    "assignee",
  ])
    if (query[key]) filters.set(key, query[key]);
  const signature = filters.toString();
  const client = useQueryClient();
  const { update } = useUrlState();
  const saving = useRef(false);
  const pointer = useRef<{
    id: number;
    x: number;
    y: number;
    itemId: string;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());
  const [lastDecision, setLastDecision] = useState<{
    item: ActivityItem;
    signature: string;
    state: ReviewState;
  } | null>(null);
  const canUndo = lastDecision?.signature === signature;
  const queryKey = ["signal-swipe", signature];
  const result = useInfiniteQuery({
    queryKey,
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        apiClient().GET("/activity", {
          params: {
            query: {
              ...Object.fromEntries(new URLSearchParams(signature)),
              page: String(pageParam),
              sort: "when",
              direction: "desc",
            },
          },
          signal,
        }),
      ),
    getNextPageParam: (page) => (page.hasNext ? page.page + 1 : undefined),
    refetchInterval: busy || dragging ? false : 30_000,
    refetchOnWindowFocus: !busy && !dragging,
  });
  useEffect(() => {
    const refresh = (event: Event) => {
      if (saving.current) return;
      const states = (event as CustomEvent<Record<string, { state: string }>>).detail;
      if (states)
        setHidden((previous) => {
          const next = new Set(previous);
          for (const [id, review] of Object.entries(states)) {
            if (
              id.startsWith("person:")
                ? review.state === "processed"
                : !!query.review && !query.review.split(",").includes(review.state)
            )
              next.add(id);
            else next.delete(id);
          }
          return next;
        });
      if (!saving.current)
        void client.invalidateQueries({
          queryKey: ["signal-swipe", signature],
        });
    };
    window.addEventListener("records:changed", refresh);
    return () => window.removeEventListener("records:changed", refresh);
  }, [client, signature, query.review]);
  const loadedItems = result.data?.pages.flatMap((page) => page.items) ?? [];
  const item = loadedItems.find(
    (entry) => !hidden.has(entry.id) && !hidden.has(`person:${entry.person_id}`),
  );
  const reviews = useReviews(item ? [item.id] : []);
  const remaining = Math.max(
    0,
    (result.data?.pages[0]?.total ?? 0) -
      loadedItems.filter((entry) => hidden.has(entry.id) || hidden.has(`person:${entry.person_id}`))
        .length,
  );
  useEffect(() => {
    if (!item && result.hasNextPage && !result.isFetching && !result.isError)
      void result.fetchNextPage();
  }, [item, result.hasNextPage, result.isFetching, result.isError, result.fetchNextPage]);

  async function decide(action: "ignore" | "acknowledge") {
    if (!item || saving.current || !reviews.loaded) return;
    const state = (reviews.states[item.id]?.state ?? "unprocessed") as ReviewState;
    saving.current = true;
    setOffset(0);
    setBusy(true);
    setOffset(action === "ignore" ? -120 : 120);
    await client.cancelQueries({ queryKey });
    const success = await reviews.act([item.id], action);
    if (success) {
      setLastDecision({ item, signature, state });
      setHidden((previous) => new Set(previous).add(item.id));
      setAnnouncement(`Signal moved to ${action === "ignore" ? "Ignored" : "Acknowledged"}.`);
      // Refresh totals; pagination skips decisions still included by the selected statuses.
      void client.invalidateQueries({ queryKey });
    } else {
      setAnnouncement("Could not save your decision. Try this signal again.");
    }
    setOffset(0);
    setBusy(false);
    saving.current = false;
  }

  async function undo() {
    if (!lastDecision || !canUndo || saving.current) return;
    saving.current = true;
    setBusy(true);
    await client.cancelQueries({ queryKey });
    const success = await reviews.act([lastDecision.item.id], reviewActions[lastDecision.state]);
    if (success) {
      setHidden((previous) => {
        const next = new Set(previous);
        next.delete(lastDecision.item.id);
        return next;
      });
      setLastDecision(null);
      setAnnouncement(`Decision undone. Signal returned to ${reviewLabels[lastDecision.state]}.`);
      void client.invalidateQueries({ queryKey });
    } else {
      setAnnouncement("Could not undo your decision. Try again.");
    }
    setBusy(false);
    saving.current = false;
  }

  const undoButton = (
    <Button
      label="Undo"
      icon={ArrowCounterClockwiseIcon}
      variant={ButtonVariant.TERTIARY}
      isDisabled={!canUndo || busy}
      onClick={() => void undo()}
    />
  );

  const onShortcut = useEffectEvent((event: KeyboardEvent) => {
    if (
      event.defaultPrevented ||
      !event.metaKey ||
      event.ctrlKey ||
      event.altKey ||
      event.shiftKey ||
      event.isComposing ||
      !["ArrowLeft", "ArrowRight", "z"].includes(event.key)
    )
      return;
    const target = event.target;
    if (
      (target instanceof HTMLElement &&
        (target.isContentEditable ||
          target.closest(
            'input,textarea,select,[role="textbox"],[role="combobox"],[role="menu"],[role="listbox"]',
          ))) ||
      document.querySelector(
        '[role="dialog"],[role="alertdialog"],[aria-haspopup][aria-expanded="true"]',
      )
    )
      return;
    const isUndo = event.key === "z";
    if (isUndo ? !canUndo : !item) return;
    event.preventDefault();
    if (!event.repeat && !pointer.current) {
      if (isUndo) void undo();
      else void decide(event.key === "ArrowLeft" ? "ignore" : "acknowledge");
    }
  });
  useEffect(() => {
    const handler = (event: KeyboardEvent) => onShortcut(event);
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <Viewport aria-label="Swipe signals">
      <Deck>
        <VisuallyHidden aria-live="polite" aria-atomic="true">
          {announcement}
        </VisuallyHidden>
        {(result.isPending || (!item && !result.isError && remaining > 0)) && <SignalLoading />}
        {result.isError && (
          <QueryState error={result.error} onRetry={() => void result.refetch()} />
        )}
        {!result.isPending && !result.isError && !item && !remaining && (
          /* A filter can hide a full queue, so only the unfiltered deck has
             earned "All caught up". */
          <SignalPlaceholder
            icon={signature ? MagnifyingGlassIcon : CheckCircleIcon}
            heading={signature ? "No matching signals" : "All caught up"}
            message={
              signature
                ? "Try changing your search or filters."
                : "No signals remain in this queue. New ones will appear here."
            }
          />
        )}
        {item && (
          <Card
            tabIndex={0}
            aria-label={`Review signal: ${item.name || item.login || "Unknown account"}: ${item.title}`}
            aria-describedby="swipe-keyboard-help"
            aria-keyshortcuts="Meta+ArrowLeft Meta+ArrowRight Meta+Z"
            aria-busy={busy}
            data-dragging={dragging}
            style={
              {
                "--gx-swipe-transform": `translateX(${offset}px) rotate(${offset / 25}deg)`,
                "--gx-swipe-opacity": busy ? 0.5 : 1,
              } as React.CSSProperties
            }
            onPointerDown={(event) => {
              if (
                saving.current ||
                !event.isPrimary ||
                event.button !== 0 ||
                (event.target as Element).closest("a,button,input,[role=button]")
              )
                return;
              pointer.current = {
                id: event.pointerId,
                x: event.clientX,
                y: event.clientY,
                itemId: item.id,
              };
              event.currentTarget.setPointerCapture(event.pointerId);
              setDragging(true);
            }}
            onPointerMove={(event) => {
              if (pointer.current?.id === event.pointerId)
                setOffset(event.clientX - pointer.current.x);
            }}
            onPointerUp={(event) => {
              const start = pointer.current;
              if (!start || start.id !== event.pointerId) return;
              pointer.current = null;
              setDragging(false);
              const dx = event.clientX - start.x;
              const dy = event.clientY - start.y;
              const threshold = Math.min(100, event.currentTarget.clientWidth / 4);
              if (
                start.itemId === item.id &&
                Math.abs(dx) >= threshold &&
                Math.abs(dx) > Math.abs(dy)
              )
                void decide(dx < 0 ? "ignore" : "acknowledge");
              else setOffset(0);
            }}
            onLostPointerCapture={() => {
              pointer.current = null;
              setDragging(false);
              if (!saving.current) setOffset(0);
            }}
          >
            <Widget
              isFlush
              variant={WidgetVariant.TERTIARY}
              header={
                <Row>
                  <ActivitySignalChip activity={item} />
                  <Text size={TextSize.BODY_SM} variant={TextVariant.TERTIARY}>
                    <RelativeTime
                      value={item.occurred_at ?? item.observed_at}
                      label={item.occurred_at ? "Occurred" : "Observed"}
                    />
                  </Text>
                </Row>
              }
              footer={
                <Actions aria-label="Signal actions">
                  {undoButton}
                  <Inline>
                    {/* Sending is always about the person; this signal is context. */}
                    {item.person_id && (
                      <SendMenu
                        key={item.person_id}
                        keys={[`person:${item.person_id}`]}
                        disabled={busy}
                        onPendingChange={(pending) => {
                          saving.current = pending;
                          setBusy(pending);
                          if (pending) void client.cancelQueries({ queryKey });
                        }}
                        onSent={() => {
                          setHidden((previous) =>
                            new Set(previous).add(`person:${item.person_id}`),
                          );
                          void client.invalidateQueries({ queryKey });
                          setAnnouncement(
                            "Person queued for sending. Their signals are removed from the inbox.",
                          );
                        }}
                      />
                    )}
                    <Button
                      label="Ignore"
                      icon={reviewIcons.ignored}
                      variant={ButtonVariant.SECONDARY}
                      isDisabled={busy || !reviews.loaded}
                      onClick={() => void decide("ignore")}
                    />
                    <Button
                      label="Acknowledge"
                      icon={reviewIcons.acknowledged}
                      variant={ButtonVariant.PRIMARY}
                      isDisabled={busy || !reviews.loaded}
                      onClick={() => void decide("acknowledge")}
                    />
                  </Inline>
                </Actions>
              }
            >
              <Content>
                <Signal aria-label="Signal details">
                  <Row>
                    <SourceLabel
                      source={item.provider}
                      label={sourceLabel(item.provider, item.context)}
                    />
                    <Button
                      label="View details"
                      variant={ButtonVariant.TERTIARY}
                      isDisabled={busy}
                      onClick={() =>
                        update({
                          activityId: item.id,
                          personId: null,
                          person: null,
                          personTab: null,
                          companyId: null,
                          companyTab: null,
                          companyPage: null,
                          runId: null,
                        })
                      }
                    />
                  </Row>
                  <Text size={TextSize.HEADING_MD}>
                    {item.title || activityVerb(item.type, item.kind, item.baseline)}
                  </Text>
                  <CommentContext context={item.commentContext} />
                  {item.body && (
                    <Description aria-label="Signal description">
                      <SourceMarkdown content={item.body} />
                    </Description>
                  )}
                  {safeUrl(item.url) && (
                    <Links>
                      <DrawerSection
                        variant={WidgetVariant.TERTIARY}
                        header="Links"
                        icon={LinkIcon}
                        defaultIsOpen
                      >
                        <DrawerRow label={sourceName(item.provider)}>
                          <DlsLink href={safeUrl(item.url)!} isExternal>
                            <Text as="span" size={TextSize.BODY_SM}>
                              {item.url}
                            </Text>
                          </DlsLink>
                        </DrawerRow>
                      </DrawerSection>
                    </Links>
                  )}
                </Signal>
                <Person aria-label="Person details">
                  <SwipePerson item={item} />
                </Person>
              </Content>
            </Widget>
          </Card>
        )}
        {!item && canUndo && <Actions aria-label="Signal actions">{undoButton}</Actions>}
        <Divider />
        <Help id="swipe-keyboard-help">
          <Text size={TextSize.CAPTION} variant={TextVariant.TERTIARY}>
            {result.data ? `${remaining} remaining` : ""}
          </Text>
          <Inline>
            {[
              { key: "←", label: "Ignore" },
              { key: "→", label: "Acknowledge" },
              { key: "Z", label: "Undo" },
            ].map(({ key, label }) => (
              <Inline key={key}>
                <Kbd hotKeys={["⌘", key]} size={KbdSize.SMALL} variant={KbdVariant.TERTIARY} />
                <Text size={TextSize.CAPTION} variant={TextVariant.TERTIARY}>
                  {label}
                </Text>
              </Inline>
            ))}
          </Inline>
        </Help>
      </Deck>
    </Viewport>
  );
}
